"use client";

import { HeartHandshake, MapPin, ShieldCheck, Users, Zap } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { FC } from "react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useFadeInAnimation } from "@/hooks/use-fade-in-animation";
import { routes } from "@/utils/routes";

const AboutContent: FC = () => {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const storyRef = useRef<HTMLDivElement | null>(null);
  const valuesRef = useRef<HTMLDivElement | null>(null);
  const valuesGridRef = useRef<HTMLDivElement | null>(null);
  const teamRef = useRef<HTMLDivElement | null>(null);
  const teamImageRef = useRef<HTMLDivElement | null>(null);
  const mapsRef = useRef<HTMLDivElement | null>(null);
  const mapsImageRef = useRef<HTMLDivElement | null>(null);
  const mapsGridRef = useRef<HTMLDivElement | null>(null);

  // Hero section - immediate entrance with subtle stagger
  const heroContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: heroRef,
    animateChildren: true,
    visibilityThreshold: 0.1, // Start when 10% visible
    animationProps: {
      duration: 1.0,
      stagger: 0.12,
      y: 40,
      ease: "power2.out",
    },
  });

  // Story section - coordinated with hero timing
  const storyContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: storyRef,
    animateChildren: true,
    visibilityThreshold: 0.2, // Start when 20% visible
    animationProps: {
      duration: 0.9,
      stagger: 0.1,
      y: 35,
      ease: "power2.out",
    },
  });

  // Values section - header animation
  const valuesContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: valuesRef,
    animateChildren: true,
    visibilityThreshold: 0.25, // Start when 25% visible
    animationProps: {
      duration: 0.8,
      stagger: 0.1,
      y: 30,
      ease: "power2.out",
    },
  });

  // Values grid - cards animation with enhanced stagger
  const valuesGridContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: valuesGridRef,
    animateChildren: true,
    visibilityThreshold: 0.1, // Start when 10% visible for better card visibility
    animationProps: {
      duration: 0.6,
      stagger: 0.1, // Shorter stagger for quicker reveal
      y: 30,
      scale: 0.95,
      ease: "power2.out",
    },
  });

  // Team section - final section with polished timing
  const teamContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: teamRef,
    animateChildren: true,
    visibilityThreshold: 0.25, // Start when 25% visible
    animationProps: {
      duration: 0.85,
      stagger: 0.08,
      y: 30,
      ease: "power2.out",
    },
  });

  // Team image - subtle scale animation
  const teamImageContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: teamImageRef,
    visibilityThreshold: 0.2, // Start when 20% visible
    animationProps: {
      duration: 1.0,
      scale: 0.9,
      ease: "power2.out",
    },
  });

  // Maps section - header animation
  const mapsContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: mapsRef,
    animateChildren: true,
    visibilityThreshold: 0.25, // Start when 25% visible
    animationProps: {
      duration: 0.8,
      stagger: 0.1,
      y: 30,
      ease: "power2.out",
    },
  });

  // Maps image - subtle scale animation
  const mapsImageContentRef = useFadeInAnimation<HTMLAnchorElement>({
    triggerRef: mapsImageRef,
    visibilityThreshold: 0.2, // Start when 20% visible
    animationProps: {
      duration: 0.9,
      scale: 0.95,
      ease: "power2.out",
    },
  });

  // Maps contact grid - staggered entrance (using grid ref directly for proper child staggering)
  const mapsGridContentRef = useFadeInAnimation<HTMLDivElement>({
    triggerRef: mapsGridRef,
    animateChildren: true,
    visibilityThreshold: 0.15, // Start when 15% visible for better visibility
    animationProps: {
      duration: 0.7,
      stagger: 0.15, // Enhanced stagger for better visual separation
      y: 30,
      ease: "power2.out",
    },
  });

  const values = [
    {
      icon: Zap,
      title: "Speed & Reliability",
      description:
        "Fast, reliable service when you need it. Order online and collect at your convenience — we make it simple.",
      color: "text-yellow-600",
    },
    {
      icon: HeartHandshake,
      title: "Friendly Service",
      description:
        "Our knowledgeable team enjoys discussing what works and helping you find the perfect fit for your goals.",
      color: "text-red-600",
    },
    {
      icon: ShieldCheck,
      title: "Quality You Can Trust",
      description:
        "Only supplements from trusted brands that we believe in. No compromises on quality.",
      color: "text-green-600",
    },
    {
      icon: MapPin,
      title: "Local & Convenient",
      description:
        "Right here in Birmingham city centre, perfectly placed for your busy lifestyle.",
      color: "text-blue-600",
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section
        className="overflow-hidden bg-background py-16 sm:py-20 lg:py-24"
        ref={heroRef}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto max-w-4xl text-center" ref={heroContentRef}>
            <h1 className="heading-display font-bold font-heading text-4xl sm:text-5xl lg:text-6xl">
              We're Here for Your Journey
            </h1>
            <p className="body-text-base mx-auto mt-6 max-w-2xl text-balance text-muted-foreground text-optimal">
              Located in Birmingham city centre, we've been supporting customers
              on their fitness journeys for years. Visit us during your lunch
              break, speak with our knowledgeable team, and find exactly what
              you need.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Button
                asChild
                className="shadow-lg shadow-primary/20 transition-all hover:shadow-primary/25 hover:shadow-xl"
                size="lg"
              >
                <Link href={routes.shop}>Explore Products</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link href={routes.shop}>Start Shopping</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section
        className="overflow-hidden bg-muted/30 py-16 sm:py-20 lg:py-24"
        ref={storyRef}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 items-center gap-x-8 gap-y-12 sm:gap-y-16 lg:grid-cols-2">
            {/* Content */}
            <div className="text-center lg:text-left" ref={storyContentRef}>
              <h2 className="heading-section font-bold font-heading text-3xl sm:text-4xl lg:text-5xl">
                Your Local Birmingham Supplement Store
              </h2>
              <p className="body-text-base mt-6 text-muted-foreground text-optimal">
                We're your trusted local partner in Birmingham city centre,
                built on speed, reliability, and commitment to your success. We
                provide quality supplements that deliver real results.
              </p>
              <p className="body-text-base mt-4 text-muted-foreground text-optimal">
                For beginners or experienced athletes, we stock only trusted
                brands with expert guidance – no gimmicks, just products to
                support your goals.
              </p>
            </div>

            {/* Image Placeholder */}
            <div
              className="relative flex h-80 w-full items-center justify-center rounded-2xl bg-primary/5 sm:h-96 lg:h-[400px]"
              ref={teamImageRef}
            >
              {/* apply team image animation to inner wrapper */}
              <div className="h-full w-full" ref={teamImageContentRef}>
                <Image
                  alt="WillPower Store team in Birmingham city centre"
                  className="rounded-2xl object-cover"
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  src="/images/store/team-photo.webp"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        className="overflow-hidden bg-background py-16 sm:py-20 lg:py-24"
        ref={valuesRef}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center" ref={valuesContentRef}>
            <h2 className="heading-section font-bold font-heading text-3xl sm:text-4xl lg:text-5xl">
              What We Stand For
            </h2>
            <p className="body-text-base mt-6 text-muted-foreground text-optimal">
              These aren't just words on a wall - they're the principles that
              guide everything we do, every day.
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-6xl" ref={valuesGridRef}>
            {/* iPad breakpoint pattern: single column mobile, two cols small phones, single on md, multi on lg */}
            <div
              className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-4"
              ref={valuesGridContentRef}
            >
              {values.map((value) => (
                <Card className="border-0 bg-card/50" key={value.title}>
                  <CardContent className="p-6 text-center">
                    <div
                      aria-hidden="true"
                      className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10"
                    >
                      {/* Decorative icon - hide from assistive tech */}
                      <value.icon
                        aria-hidden="true"
                        className={`h-6 w-6 ${value.color}`}
                        focusable={false}
                      />
                    </div>
                    <h3 className="heading-section font-heading font-semibold text-lg">
                      {value.title}
                    </h3>
                    <p className="body-text-small mt-3 text-muted-foreground text-optimal">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="overflow-hidden bg-muted/30 py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto max-w-4xl text-center" ref={mapsRef}>
            <div ref={mapsContentRef}>
              <h2 className="heading-section mb-6 font-bold font-heading text-3xl sm:text-4xl lg:text-5xl">
                Visit Us in Birmingham City Centre
              </h2>
              <p className="body-text-base mx-auto mb-12 max-w-2xl text-muted-foreground text-optimal">
                Located in the heart of Birmingham's Great Western Arcade, we're
                perfectly positioned for your busy lifestyle. Click the map to
                get directions.
              </p>
            </div>

            {/* Interactive Map */}
            <div
              className="relative mx-auto max-w-2xl lg:max-w-3xl"
              ref={mapsImageRef}
            >
              {/* apply map image animation directly to the interactive anchor */}
              <a
                aria-label="Open WillPower Store location on Google Maps (opens in new tab)"
                className="group relative block overflow-hidden rounded-2xl transition-transform duration-300 hover:scale-[1.02] focus:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                href="https://maps.google.com/maps?q=13+Great+Western+Arcade,+Birmingham,+B32QD,+UK"
                ref={mapsImageContentRef}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Image
                  alt="Map showing WillPower Store location at 13 Great Western Arcade, Birmingham city centre"
                  className="h-auto w-full object-cover transition-opacity duration-300 group-hover:opacity-90"
                  height={375}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                  src="/images/store/maps.webp"
                  width={600}
                />
              </a>
            </div>

            {/* Contact Information Grid */}
            <div
              className="mx-auto mt-8 grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-1 md:gap-12 lg:grid-cols-3"
              ref={mapsGridContentRef}
            >
              {/* Address Section */}
              <div className="flex h-full flex-col justify-start text-center">
                <h3 className="mb-3 font-heading font-semibold text-lg">
                  Address
                </h3>
                <p className="body-text-small text-muted-foreground text-optimal leading-relaxed">
                  13 Great Western Arcade
                  <br />
                  Birmingham, B32QD
                  <br />
                  United Kingdom
                </p>
              </div>

              {/* Opening Hours Section */}
              <div className="flex h-full flex-col justify-start text-center">
                <h3 className="mb-3 font-heading font-semibold text-lg">
                  Opening Hours
                </h3>
                <p className="body-text-small text-muted-foreground text-optimal leading-relaxed">
                  Monday - Saturday
                  <br />
                  11:00 AM - 4:00 PM
                  <br />
                  Sunday: Closed
                </p>
              </div>

              {/* Contact Section */}
              <div className="flex h-full flex-col justify-start text-center">
                <h3 className="mb-3 font-heading font-semibold text-lg">
                  Contact
                </h3>
                <p className="body-text-small text-muted-foreground text-optimal leading-relaxed">
                  <a
                    className="rounded-sm transition-colors hover:text-primary focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    href="mailto:willpowerwellbeing@hotmail.com"
                  >
                    willpowerwellbeing@hotmail.com
                  </a>
                  <br />
                  <a
                    className="rounded-sm transition-colors hover:text-primary focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    href="https://instagram.com/willpowergymstoreuk"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    @willpowergymstoreuk
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutContent;
